import { useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Chip, DialogActions, styled } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Task, TaskStatus } from 'types/task';
import { formatScheduleDisplay } from 'utils/formatScheduleDisplay';

import { DeleteModal } from 'components/DeleteModal';

import { useApi } from '../hooks/useApi';

export interface SimpleDialogProps {
  open: boolean;
  task: Task;
  onClose: (value: boolean) => void;
  refetch: () => void;
}

const CHIP_COLOR = {
  [TaskStatus.ERROR]: 'error',
  [TaskStatus.COMPLETED]: 'success',
  [TaskStatus.PENDING]: 'warning',
};

const Span = styled('span')`
  font-weight: bold;
`;

export const TaskInfoDialog = ({ onClose, task, open, refetch }: SimpleDialogProps) => {
  const { apiFetch } = useApi();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    onClose(false);
  };

  const handleCloseConfirmModal = () => {
    setOpenConfirmModal(false);
  };

  const deleteTask = async () => {
    setIsLoading(true);
    handleClose();

    try {
      const res = await apiFetch(`tasks/${task.id}`, {
        method: 'DELETE',
      });

      const data = await res.json();
      refetch();

      toast.success(data.message, {
        position: 'top-right',
        autoClose: 3000,
      });
      handleCloseConfirmModal();
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : 'An unexpected error occurred';

      toast.error(errorMessage, {
        position: 'top-right',
        autoClose: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!task) {
    return null;
  }

  return (
    <>
      <Dialog
        onClose={handleClose}
        open={open}
        fullWidth
      >
        <Stack
          gap={2}
          pt={4}
          pb={2}
          px={3}
        >
          <Typography variant="h5">Task information</Typography>
          <Stack gap={2}>
            <Typography variant="body1">
              <Span>Agent:</Span> {task.agent_name}
            </Typography>
            <Typography variant="body1">
              <Span>Schedule:</Span> {formatScheduleDisplay(task.schedule, task.schedule_at)}
            </Typography>
            <Typography variant="body1">
              <Span>Status:</Span>
              <Chip
                label={task.status}
                variant="filled"
                color={CHIP_COLOR[task.status] as 'error' | 'success' | 'warning'}
                sx={{
                  ml: 2,
                }}
              />
            </Typography>
          </Stack>
          <Typography variant="body1">
            <Span>Instructions:</Span> {task.instruction}
          </Typography>
          {task.error && (
            <Typography variant="body1">
              <Span>Error message:</Span> {task.error.errorMessage}
            </Typography>
          )}
        </Stack>
        <DialogActions>
          <Button
            variant="contained"
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={() => setOpenConfirmModal(true)}
            color="error"
          >
            Delete event
          </Button>
        </DialogActions>
      </Dialog>
      <DeleteModal
        title="Are you sure you want to delete this task and all its recurring instances?"
        onClose={handleCloseConfirmModal}
        onConfirm={deleteTask}
        isOpen={openConfirmModal}
        isLoading={isLoading}
      />
    </>
  );
};
